import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Card, 
  Statistic, 
  Row, 
  Col, 
  DatePicker, 
  InputNumber, 
  Button, 
  Select, 
  Space, 
  Typography, 
  Divider,
  Tag,
  Tooltip,
  Spin,
  Alert,
  Modal,
  Tabs
} from 'antd';
import { 
  DollarOutlined, 
  TruckOutlined, 
  BarChartOutlined,
  CalendarOutlined,
  FilterOutlined,
  CalculatorOutlined
} from '@ant-design/icons';
import { DataTable, MoneyCell } from '../../ui';
import { formatCurrency } from '../../utils/dateUtils';
import { useLazyGetRouteCostAnalysisQuery, useLazyGetCalculatorCostAnalysisQuery, useLazyGetPopularDirectionsQuery, useLazyGetCitiesQuery } from '../../store/api/costAnalysisApi';
import { DirectionCostSummary } from '../../store/api/costAnalysisApi';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Option } = Select;

const RouteCostAnalysis: React.FC = () => {
  const [activeTab, setActiveTab] = useState<string>('real');
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs]>([
    dayjs().subtract(6, 'month'),
    dayjs()
  ]);
  const [city1, setCity1] = useState<string>('');
  const [city2, setCity2] = useState<string>('');
  const [groupByDirection, setGroupByDirection] = useState<boolean>(true);
  const [minWeightKg, setMinWeightKg] = useState<number | undefined>(undefined);
  const [maxWeightKg, setMaxWeightKg] = useState<number | undefined>(undefined);
  const [minVolumeM3, setMinVolumeM3] = useState<number | undefined>(undefined);
  const [maxVolumeM3, setMaxVolumeM3] = useState<number | undefined>(undefined);
  const [limit] = useState<number>(1000);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [modalRoutes, setModalRoutes] = useState<any[]>([]);

  const [triggerAnalysis, { data: analysisData, isLoading: analysisLoading, error: analysisError }] = useLazyGetRouteCostAnalysisQuery();
  const [triggerCalculatorAnalysis, { data: calculatorAnalysisData, isLoading: calculatorAnalysisLoading, error: calculatorAnalysisError }] = useLazyGetCalculatorCostAnalysisQuery();
  const [triggerDirections] = useLazyGetPopularDirectionsQuery();
  const [triggerCities, { data: citiesData, isLoading: citiesLoading }] = useLazyGetCitiesQuery();

  // Загружаем популярные направления и города при инициализации
  useEffect(() => {
    triggerDirections({ limit: 20 });
    triggerCities();
  }, [triggerDirections, triggerCities]);

  const handleAnalysis = useCallback(() => {
    const params = {
      fromDate: dateRange[0].format('YYYY-MM-DD'),
      toDate: dateRange[1].format('YYYY-MM-DD'),
      city1: city1 || undefined,
      city2: city2 || undefined,
      minWeightKg,
      maxWeightKg,
      minVolumeM3,
      maxVolumeM3,
      groupByDirection,
      limit
    };

    if (activeTab === 'real') {
      triggerAnalysis(params);
    } else {
      triggerCalculatorAnalysis(params);
    }
  }, [
    dateRange,
    city1,
    city2,
    minWeightKg,
    maxWeightKg,
    minVolumeM3,
    maxVolumeM3,
    groupByDirection,
    limit,
    activeTab,
    triggerAnalysis,
    triggerCalculatorAnalysis,
  ]);

  // Выполняем анализ при изменении параметров
  useEffect(() => {
    handleAnalysis();
  }, [handleAnalysis]);

  const formatNumber = (num: number, decimals: number = 2) => {
    return new Intl.NumberFormat('ru-RU', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Колонки для таблицы направлений (динамические в зависимости от вкладки)
  const directionColumns = useMemo(() => {
    const isCalculator = activeTab === 'calculator';
    return [
      {
        title: 'Направление',
        dataIndex: 'direction',
        key: 'direction',
        width: 200,
        render: (direction: string) => (
          <Text strong style={{ fontSize: '14px' }}>{direction}</Text>
        )
      },
      {
        title: isCalculator ? 'Количество расчетов' : 'Количество маршрутов',
        dataIndex: 'routeCount',
        key: 'routeCount',
        width: 120,
        align: 'center' as const,
        render: (count: number) => (
          <Tag color="blue">{count}</Tag>
        )
      },
      {
        title: isCalculator ? 'Общая ставка клиенту' : 'Общая стоимость',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 140,
        align: 'right' as const,
        render: (amount: number) => (
          <Text strong>
            <MoneyCell value={amount} type={isCalculator ? 'income' : 'neutral'} />
          </Text>
        )
      },
      {
        title: 'Общий вес (кг)',
        dataIndex: 'totalWeight',
        key: 'totalWeight',
        width: 120,
        align: 'right' as const,
        render: (weight: number) => formatNumber(weight, 0)
      },
      {
        title: 'Общий объем (м³)',
        dataIndex: 'totalVolume',
        key: 'totalVolume',
        width: 120,
        align: 'right' as const,
        render: (volume: number) => formatNumber(volume, 2)
      },
      {
        title: isCalculator ? 'Средняя ставка за кг' : 'Средняя стоимость за кг',
        dataIndex: 'averageCostPerKg',
        key: 'averageCostPerKg',
        width: 160,
        align: 'right' as const,
        render: (cost: number) =>
          cost > 0 ? <MoneyCell value={cost} type="income" /> : <Text type="secondary">—</Text>
      },
      {
        title: isCalculator ? 'Средняя ставка за м³' : 'Средняя стоимость за м³',
        dataIndex: 'averageCostPerM3',
        key: 'averageCostPerM3',
        width: 160,
        align: 'right' as const,
        render: (cost: number) =>
          cost > 0 ? <MoneyCell value={cost} type="income" /> : <Text type="secondary">—</Text>
      },
      {
        title: 'Диапазон за кг',
        key: 'costRangeKg',
        width: 140,
        align: 'center' as const,
        render: (record: DirectionCostSummary) => (
          <Tooltip title={`Мин: ${formatCurrency(record.minCostPerKg)}, Макс: ${formatCurrency(record.maxCostPerKg)}`}>
            <Text type="secondary">
              <MoneyCell value={record.minCostPerKg} type="neutral" /> —{' '}
              <MoneyCell value={record.maxCostPerKg} type="neutral" />
            </Text>
          </Tooltip>
        )
      }
    ];
  }, [activeTab]);

  // Колонки для детальной таблицы маршрутов (динамические в зависимости от вкладки)
  const routeColumns = useMemo(() => {
    const isCalculator = activeTab === 'calculator';
    return [
      {
        title: isCalculator ? 'Расчёт' : 'Маршрут',
        dataIndex: 'routeNumber',
        key: 'routeNumber',
        width: 120,
      },
      {
        title: 'Направление',
        key: 'direction',
        width: 200,
        render: (record: any) => `${record.loadingAddress} → ${record.unloadingAddress}`
      },
      {
        title: isCalculator ? 'Ставка клиенту' : 'Ставка',
        dataIndex: 'totalAmount',
        key: 'totalAmount',
        width: 120,
        align: 'right' as const,
        render: (amount: number) => <MoneyCell value={amount} type={isCalculator ? 'income' : 'neutral'} />
      },
    {
      title: 'Вес (кг)',
      dataIndex: 'totalWeight',
      key: 'totalWeight',
      width: 100,
      align: 'right' as const,
      render: (weight: number) => formatNumber(weight, 0)
    },
    {
      title: 'Объем (м³)',
      dataIndex: 'totalVolume',
      key: 'totalVolume',
      width: 100,
      align: 'right' as const,
      render: (volume: number) => formatNumber(volume, 2)
    },
      {
        title: isCalculator ? 'Ставка за кг' : 'За кг',
        dataIndex: 'costPerKg',
        key: 'costPerKg',
        width: 100,
        align: 'right' as const,
        render: (cost: number) =>
          cost > 0 ? <MoneyCell value={cost} type="income" /> : <Text type="secondary">—</Text>
      },
      {
        title: isCalculator ? 'Ставка за м³' : 'За м³',
        dataIndex: 'costPerM3',
        key: 'costPerM3',
        width: 100,
        align: 'right' as const,
        render: (cost: number) =>
          cost > 0 ? <MoneyCell value={cost} type="income" /> : <Text type="secondary">—</Text>
      },
      {
        title: 'Дата',
        dataIndex: 'date',
        key: 'date',
        width: 120,
        render: (date: string) => dayjs(date).format('DD.MM.YYYY')
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={status === 'calculated' ? 'purple' : status === 'completed' ? 'green' : status === 'in_progress' ? 'blue' : 'orange'}>
            {status === 'calculated' ? 'Расчёт' : status}
          </Tag>
        )
      }
    ];
  }, [activeTab]);

  return (
    <>
      {/* Фильтры */}
      <Card style={{ marginBottom: '24px' }}>
        <Title level={4}>
          <FilterOutlined style={{ marginRight: '8px' }} />
          Параметры анализа
        </Title>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Период</Text>
              <RangePicker
                value={dateRange}
                onChange={(dates) => dates && setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs])}
                style={{ width: '100%' }}
                format="DD.MM.YYYY"
              />
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Город отправления</Text>
              <Select
                value={city1}
                onChange={setCity1}
                style={{ width: '100%' }}
                placeholder="Выберите город"
                showSearch
                loading={citiesLoading}
                allowClear
              >
                {citiesData?.map(city => (
                  <Option key={city} value={city}>{city}</Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Город назначения</Text>
              <Select
                value={city2}
                onChange={setCity2}
                style={{ width: '100%' }}
                placeholder="Выберите город"
                showSearch
                loading={citiesLoading}
                allowClear
              >
                {citiesData?.map(city => (
                  <Option key={city} value={city}>{city}</Option>
                ))}
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Группировка</Text>
              <Select
                value={groupByDirection}
                onChange={setGroupByDirection}
                style={{ width: '100%' }}
              >
                <Option value={true}>По направлениям</Option>
                <Option value={false}>Все маршруты</Option>
              </Select>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Вес (кг), диапазон</Text>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  placeholder="от"
                  value={minWeightKg}
                  onChange={(value) => setMinWeightKg(value === null ? undefined : Number(value))}
                  style={{ width: '50%' }}
                />
                <InputNumber
                  placeholder="до"
                  value={maxWeightKg}
                  onChange={(value) => setMaxWeightKg(value === null ? undefined : Number(value))}
                  style={{ width: '50%', marginLeft: 8 }}
                />
              </Space.Compact>
            </Space>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text strong>Объём (м³), диапазон</Text>
              <Space.Compact style={{ width: '100%' }}>
                <InputNumber
                  placeholder="от"
                  value={minVolumeM3}
                  onChange={(value) => setMinVolumeM3(value === null ? undefined : Number(value))}
                  style={{ width: '50%' }}
                />
                <InputNumber
                  placeholder="до"
                  value={maxVolumeM3}
                  onChange={(value) => setMaxVolumeM3(value === null ? undefined : Number(value))}
                  style={{ width: '50%', marginLeft: 8 }}
                />
              </Space.Compact>
            </Space>
          </Col>
        </Row>
        <Divider />
        <Button 
          type="primary" 
          icon={<BarChartOutlined />}
          onClick={handleAnalysis}
          loading={analysisLoading}
        >
          Обновить анализ
        </Button>
      </Card>

      {/* Вкладки и результаты анализа */}
      <Tabs
        activeKey={activeTab}
        onChange={(key) => {
          setActiveTab(key);
          // Запускаем анализ при переключении вкладки
          setTimeout(() => handleAnalysis(), 100);
        }}
        items={[
          {
            key: 'real',
            label: (
              <span>
                <TruckOutlined /> Реальные перевозки
              </span>
            ),
            children: (
              <>
                {/* Ошибка */}
                {analysisError && (
                  <Alert
                    message="Ошибка загрузки данных"
                    description="Не удалось загрузить данные анализа стоимости. Проверьте параметры и попробуйте снова."
                    type="error"
                    style={{ marginBottom: '24px' }}
                  />
                )}

                {/* Результаты анализа реальных перевозок */}
                {analysisData ? (
                  <>
                    {/* Общая статистика */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Всего маршрутов"
                            value={analysisData.totalRoutes}
                            prefix={<TruckOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Общая стоимость"
                            value={analysisData.totalAmount}
                            formatter={(value) => formatCurrency(Number(value))}
                            prefix={<DollarOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Средняя стоимость за кг"
                            value={analysisData.averageCostPerKg}
                            formatter={(value) => formatCurrency(Number(value))}
                            prefix={<DollarOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Средняя стоимость за м³"
                            value={analysisData.averageCostPerM3}
                            formatter={(value) => formatCurrency(Number(value))}
                            prefix={<DollarOutlined />}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Таблица результатов */}
                    <Card>
                      <Title level={4}>
                        <CalendarOutlined style={{ marginRight: '8px' }} />
                        Результаты анализа
                        {analysisData.period && (
                          <Text type="secondary" style={{ marginLeft: '16px', fontSize: '14px' }}>
                            Период: {dayjs(analysisData.period.from).format('DD.MM.YYYY')} - {dayjs(analysisData.period.to).format('DD.MM.YYYY')}
                          </Text>
                        )}
                      </Title>
                      
                      {analysisLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin size="large" />
                          <div style={{ marginTop: '16px' }}>Загрузка данных...</div>
                        </div>
                      ) : (
                        <DataTable
                          columns={groupByDirection ? directionColumns : routeColumns}
                          dataSource={groupByDirection ? analysisData.summary : analysisData.summary.flatMap(s => s.routes) as any}
                          rowKey={groupByDirection ? 'direction' : 'routeId'}
                          pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                          }}
                          scroll={{ x: 'max-content' }}
                          size="small"
                          onRow={(record: any) => ({
                            onClick: () => {
                              if (!groupByDirection) return;
                              setModalTitle(record.direction);
                              setModalRoutes(record.routes || []);
                              setModalOpen(true);
                            },
                            style: groupByDirection ? { cursor: 'pointer' } : {}
                          })}
                        />
                      )}
                    </Card>
                  </>
                ) : null}
              </>
            )
          },
          {
            key: 'calculator',
            label: (
              <span>
                <CalculatorOutlined /> Расчеты из калькулятора
              </span>
            ),
            children: (
              <>
                {/* Ошибка */}
                {calculatorAnalysisError && (
                  <Alert
                    message="Ошибка загрузки данных"
                    description="Не удалось загрузить данные из калькулятора. Проверьте параметры и попробуйте снова."
                    type="error"
                    style={{ marginBottom: '24px' }}
                  />
                )}

                {/* Результаты анализа из калькулятора */}
                {calculatorAnalysisData ? (
                  <>
                    {/* Общая статистика */}
                    <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Всего расчетов"
                            value={calculatorAnalysisData.totalRoutes}
                            prefix={<CalculatorOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Общая ставка клиенту"
                            value={calculatorAnalysisData.totalAmount}
                            formatter={(value) => formatCurrency(Number(value))}
                            prefix={<DollarOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Средняя ставка за кг"
                            value={calculatorAnalysisData.averageCostPerKg}
                            formatter={(value) => formatCurrency(Number(value))}
                            prefix={<DollarOutlined />}
                          />
                        </Card>
                      </Col>
                      <Col xs={24} sm={12} md={6}>
                        <Card>
                          <Statistic
                            title="Средняя ставка за м³"
                            value={calculatorAnalysisData.averageCostPerM3}
                            formatter={(value) => formatCurrency(Number(value))}
                            prefix={<DollarOutlined />}
                          />
                        </Card>
                      </Col>
                    </Row>

                    {/* Таблица результатов */}
                    <Card>
                      <Title level={4}>
                        <CalculatorOutlined style={{ marginRight: '8px' }} />
                        Ставки клиенту из калькулятора (AUTO сегменты)
                        {calculatorAnalysisData.period && (
                          <Text type="secondary" style={{ marginLeft: '16px', fontSize: '14px' }}>
                            Период: {dayjs(calculatorAnalysisData.period.from).format('DD.MM.YYYY')} - {dayjs(calculatorAnalysisData.period.to).format('DD.MM.YYYY')}
                          </Text>
                        )}
                      </Title>
                      
                      {calculatorAnalysisLoading ? (
                        <div style={{ textAlign: 'center', padding: '40px' }}>
                          <Spin size="large" />
                          <div style={{ marginTop: '16px' }}>Загрузка данных...</div>
                        </div>
                      ) : (
                        <DataTable
                          columns={groupByDirection ? directionColumns : routeColumns}
                          dataSource={groupByDirection ? calculatorAnalysisData.summary : calculatorAnalysisData.summary.flatMap(s => s.routes) as any}
                          rowKey={groupByDirection ? 'direction' : 'routeId'}
                          pagination={{
                            pageSize: 20,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} записей`,
                          }}
                          scroll={{ x: 'max-content' }}
                          size="small"
                          onRow={(record: any) => ({
                            onClick: () => {
                              if (!groupByDirection) return;
                              setModalTitle(record.direction);
                              setModalRoutes(record.routes || []);
                              setModalOpen(true);
                            },
                            style: groupByDirection ? { cursor: 'pointer' } : {}
                          })}
                        />
                      )}
                    </Card>
                  </>
                ) : null}
              </>
            )
          }
        ]}
      />

      <Modal
        title={`Маршруты: ${modalTitle}`}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={1000}
      >
        <DataTable
          columns={routeColumns}
          dataSource={modalRoutes}
          rowKey={'routeId'}
          pagination={{ pageSize: 10 }}
          size="small"
        />
      </Modal>
    </>
  );
};

export default RouteCostAnalysis;
